import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { skipWhile, take } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { CompanyService } from 'src/app/core/services/company.service';
import { FilialAdminService } from 'src/app/core/services/filial.admin.service';
import { UserService } from 'src/app/core/services/user.service';
import { WorkerService } from 'src/app/core/services/worker.service';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.css'],
})
export class UserFormComponent implements OnInit {
  user: any = {};
  isLoading = false;
  companyId: string;
  userId: string;
  isLoadingForm = false;
  userType = '';

  constructor(
    private companyService: CompanyService,
    private userService: UserService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private filialAdminService: FilialAdminService,
    private workerService: WorkerService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;

    this.route.parent.params.subscribe((res) => {
      console.log(res)
      this.companyId = res.id;
    });

    this.authService.LoggedinUser.pipe(
      skipWhile((user) => user === null),
      take(1)
    ).subscribe((user) => {
      if(user.role.name === 'user' || user.role.name === 'filialAdmin') {
        this.companyId = user.companyId;
      }
    });

    if (this.router.url.includes('admins')) {
      this.userType = 'filialAdmin';
    } else if (this.router.url.includes('users')) {
      this.userType = 'user';
    } else {
      this.userType = 'worker';
    }

    if (this.router.url.includes('new')) {
      this.user.isNew = true;
      this.isLoading = false;
    } else {
      this.userId = this.route.snapshot.params.userId;

      switch (this.userType) {
        case 'user':
          this.loadUser();
          break;
        case 'filialAdmin':
          this.loadFilialAdmin();
          break;
        case 'worker':
          this.loadWorker();
          break;
      }
    }
  }

  loadUser() {
    this.userService.getCompanyUser(this.companyId, this.userId).subscribe(
      (res) => {
        this.user = res;
        this.user.isNew = false;
        this.isLoading = false;
      },
      (err) => {
        this.isLoading = false;
      }
    );
  }

  loadFilialAdmin() {
    this.filialAdminService
      .getCompanyFilialAdmin(this.companyId, this.userId)
      .subscribe(
        (res) => {
          this.user = res;
          this.user.isNew = false;
          this.isLoading = false;
        },
        (err) => {
          this.isLoading = false;
        }
      );
  }

  loadWorker() {
    this.workerService.getCompanyWorker(this.companyId, this.userId).subscribe(
      (res) => {
        this.user = res;
        this.user.isNew = false;
        this.isLoading = false;
      },
      (err) => {
        this.isLoading = false;
      }
    );
  }

  onAddUser(form: NgForm) {
    let body = {};

    if (!form.valid) {
      this.translate.get('global.requiredFields').subscribe((res: string) => {
        this.toastr.error(res);
      });
      return false;
    }

    Object.entries(form.form.value).forEach(([key, value], index) => {
      if (value) {
        body[key] = value;
      }
    });

    this.isLoadingForm = true;

    switch (this.userType) {
      case 'user':
        this.addUser(this.companyId, body);
        break;
      case 'filialAdmin':
        this.addFilialAdmin(this.companyId, body);
        break;
      case 'worker':
        this.addWorker(this.companyId, body);
        break;
    }
  }

  addUser(companyId, body) {
    this.userService.createUser(companyId, body).subscribe(
      (res) => {
        this.translate
          .get('companies.companyAdmin.adminCreated')
          .subscribe((res: string) => {
            this.toastr.success(res);
          });

        this.navigate();
      },
      (err) => {
        this.toastr.error(err.error.description);
        this.isLoadingForm = false;
      }
    );
  }

  addFilialAdmin(companyId, body) {
    this.filialAdminService.createFilialAdmin(companyId, body).subscribe(
      (res) => {
        this.translate
          .get('companies.companyAdmin.adminCreated')
          .subscribe((res: string) => {
            this.toastr.success('Success');
          });

        this.navigate();
      },
      (err) => {
        this.toastr.error(err.error.description);
        this.isLoadingForm = false;
      }
    );
  }

  addWorker(companyId, body) {
    this.workerService.createCompanyWorker(companyId, body).subscribe(
      (res) => {
        this.translate
          .get('companies.companyAdmin.adminCreated')
          .subscribe((res: string) => {
            this.toastr.success('Success');
          });

        this.navigate();
      },
      (err) => {
        this.toastr.error(err.error.description);
        this.isLoadingForm = false;
      }
    );
  }

  navigate() {
    this.user = {};
    this.user.isNew = true;
    this.isLoadingForm = false;

    this.router.navigate(['..'], { relativeTo: this.route });
  }

  onEdit(form: NgForm) {
    if (!form.valid) {
      this.translate.get('global.requiredFields').subscribe((res: string) => {
        this.toastr.error(res);
      });
      return;
    }

    const editData = form.form.value;

    const body = {};

    Object.keys(this.user).forEach((key) => {
      if (key in editData) {
        if (this.user[key] !== editData[key]) {
          body[key] = editData[key] === '' ? null : editData[key];
        }
      }
    });

    if (Object.keys(body).length === 0 && body.constructor === Object) {
      return;
    }

    console.log(body);
    this.isLoadingForm = true;

    switch (this.userType) {
      case 'user':
        this.editUser(this.companyId, body);
        break;
      case 'filialAdmin':
        this.editFilialAdmin(this.companyId, body);
        break;
      case 'worker':
        this.editWorker(this.companyId, body);
        break;
    }
  }

  editUser(companyId, body) {
    this.userService.editUser(companyId, this.userId, body).subscribe(
      (res) => {
        this.translate
          .get('companies.companyAdmin.adminEdited')
          .subscribe((res: string) => {
            this.toastr.success(res);
          });

        this.isLoadingForm = false;
        this.router.navigate(['../..'], { relativeTo: this.route });
      },
      (err) => {
        this.isLoadingForm = false;
      }
    );
  }

  editFilialAdmin(companyId, body) {
    this.filialAdminService
      .updateCompanyFilialAdmin(companyId, this.userId, body)
      .subscribe(
        (res) => {
          this.translate
            .get('companies.companyAdmin.adminEdited')
            .subscribe((res: string) => {
              this.toastr.success('Success');
            });

          this.isLoadingForm = false;
          this.router.navigate(['../..'], { relativeTo: this.route });
        },
        (err) => {
          this.isLoadingForm = false;
        }
      );
  }

  editWorker(companyId, body) {
    this.workerService
      .updateCompanyWorker(companyId, this.userId, body)
      .subscribe(
        (res) => {
          this.translate
            .get('companies.companyAdmin.adminEdited')
            .subscribe((res: string) => {
              this.toastr.success('Success');
            });

          this.isLoadingForm = false;
          this.router.navigate(['../..'], { relativeTo: this.route });
        },
        (err) => {
          this.isLoadingForm = false;
        }
      );
  }
}
